// adminAuth.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

// Add mock delay utility
const mockDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Toggle mock mode ON (true) or OFF (false)
const MOCK_MODE = true

// ------------------------------
// TYPES
// ------------------------------
interface AdminAuthState {
  phone: string
  otp: string

  sendAdminOtp: any | null
  sendAdminOtpStatus: APIStatus
  sendAdminOtpError: string | null

  verifyAdminOtp: any | null
  verifyAdminOtpStatus: APIStatus
  verifyAdminOtpError: string | null

  status: APIStatus
  error: string | null
}

const initialState: AdminAuthState = {
  // Form state
  phone: '',
  otp: '',

  // Auth data

  // Async states
  sendAdminOtp: null,
  sendAdminOtpStatus: APIStatus.idle,
  sendAdminOtpError: null,

  verifyAdminOtp: null,
  verifyAdminOtpStatus: APIStatus.idle,
  verifyAdminOtpError: null,

  status: APIStatus.idle,
  error: null,
}

// ------------------------------
// THUNKS
// ------------------------------

type OtpPayload = { phone: string }
type VerifyPayload = { phone: string; code: string }

export const sendAdminOtpAsync = createAsyncThunk('adminAuth/sendOtp', async ({ phone }: OtpPayload, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { phone })
    if (response && response.data.code === 'success' && response.data) {
      return thunkAPI.fulfillWithValue(response.data)
    } else {
      return thunkAPI.rejectWithValue(response?.data?.message || 'Failed to send OTP')
    }
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to send OTP')
  }
})

export const verifyAdminOtpAsync = createAsyncThunk(
  'adminAuth/verifyOtp',
  async ({ phone, code }: VerifyPayload, thunkAPI) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login/verify`, { phone, code })

      if (response && response.data.code === 'success' && response.data.data) {
        localStorage.setItem(AppConstant.ADMIN_TOKEN, response.data.data.accessToken)

        return thunkAPI.fulfillWithValue(response.data.data)
      } else if (response && response.data.data === null) {
        return thunkAPI.rejectWithValue(response.data.message)
      }

      return thunkAPI.fulfillWithValue({ admin_token: response.data.data.accessToken })
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Verification failed')
    }
  },
)

// ------------------------------
// SLICE
// ------------------------------

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    // ------------------------------
    // FORM REDUCERS
    // ------------------------------
    setAdminPhone(state, action: PayloadAction<string>) {
      state.phone = action.payload
    },
    setAdminOtp(state, action: PayloadAction<string>) {
      state.otp = action.payload
    },
    resetAdminPhone(state) {
      state.phone = ''
    },
    resetAdminOtp(state) {
      state.otp = ''
    },
    resetAdminForm() {
      return {
        ...initialState,
      }
    },

    // ------------------------------
    // AUTH RESET REDUCER
    // ------------------------------
    clearAdminAuth(state) {
      localStorage.removeItem('admin_token')
    },
    resetAdminOtpStatus(state) {
      state.sendAdminOtpStatus = APIStatus.idle
    },
    resetVerifyAdminOtpError(state) {
      state.verifyAdminOtpError = null
    },
    resetSendAdminOtpError(state) {
      state.sendAdminOtpError = null
    },
  },

  extraReducers: (builder) => {
    builder
      // --------------------
      // SEND OTP
      // --------------------
      .addCase(sendAdminOtpAsync.pending, (state) => {
        state.sendAdminOtpStatus = APIStatus.pending
        state.sendAdminOtpError = null
      })
      .addCase(sendAdminOtpAsync.fulfilled, (state, action) => {
        state.sendAdminOtpStatus = APIStatus.fulfilled
        state.sendAdminOtp = action.payload?.data || null
      })
      .addCase(sendAdminOtpAsync.rejected, (state, action) => {
        state.sendAdminOtpStatus = APIStatus.rejected
        state.sendAdminOtpError = (action.payload as string) || 'Failed to send OTP'
      })

      // --------------------
      // VERIFY OTP
      // --------------------
      .addCase(verifyAdminOtpAsync.pending, (state) => {
        state.verifyAdminOtpStatus = APIStatus.pending
        state.verifyAdminOtpError = null
      })
      .addCase(verifyAdminOtpAsync.fulfilled, (state, action) => {
        state.verifyAdminOtpStatus = APIStatus.fulfilled
        state.verifyAdminOtp = action.payload?.admin_token || null
      })
      .addCase(verifyAdminOtpAsync.rejected, (state, action) => {
        state.verifyAdminOtpStatus = APIStatus.rejected
        state.verifyAdminOtpError = (action.payload as string) || 'Failed to verify OTP'
      })
  },
})

// Export actions
export const {
  setAdminPhone,
  setAdminOtp,
  resetAdminOtp,
  resetAdminPhone,
  resetAdminForm,
  clearAdminAuth,
  resetAdminOtpStatus,
  resetSendAdminOtpError,
  resetVerifyAdminOtpError,
} = adminAuthSlice.actions

// Export reducer
export default adminAuthSlice.reducer
